/**
 * Logo — thin wrapper around the Finnly brand assets in frontend/public/brand/.
 *
 * Variants
 * --------
 *   <Logo variant="mark" height={28} />      // fox head — favicons, top bars
 *   <Logo variant="wordmark" height={36} />  // "finnly" inline — email signatures, footers
 *   <Logo variant="lockup" height={80} />    // fox + book + wordmark — login, splash
 *   <Logo variant="avatar" size={64} />      // square parchment — chat bubbles
 *
 * Backwards-compat: ``variant="full"`` is kept as an alias for ``"lockup"`` so
 * older call sites don't break.
 */
type Variant = 'mark' | 'wordmark' | 'lockup' | 'avatar' | 'full';

interface Props {
  variant?: Variant;
  /** Rendered pixel height for non-square variants. Width is computed from aspect ratio. */
  height?: number;
  /** Square pixel size for the avatar variant. */
  size?: number;
  className?: string;
}

const SRC: Record<Exclude<Variant, 'full'>, string> = {
  mark: '/brand/finnly-mark.png',
  wordmark: '/brand/finnly-wordmark.png',
  lockup: '/brand/finnly-lockup.png',
  avatar: '/brand/finnly-avatar.png',
};

export default function Logo({ variant = 'lockup', height = 48, size, className }: Props) {
  const resolved: Exclude<Variant, 'full'> = variant === 'full' ? 'lockup' : variant;
  const src = SRC[resolved];
  const isSquare = resolved === 'avatar';
  const dimension = isSquare ? (size ?? 64) : undefined;

  return (
    <img
      src={src}
      alt="Finnly"
      className={className}
      style={
        isSquare
          ? { width: dimension, height: dimension, display: 'block', borderRadius: 12 }
          : { height, width: 'auto', display: 'block', imageRendering: 'auto' }
      }
      onError={(e) => {
        (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
      }}
    />
  );
}
